<script setup>
import { ref, computed, markRaw, onMounted, provide } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';
import TeacherPage1 from '@/views/result/MLSRD-2026/teacher/Page1.vue';
import TeacherPage2 from '@/views/result/MLSRD-2026/teacher/Page2.vue';
import TeacherPage4 from '@/views/result/MLSRD-2026/teacher/Page4.vue';
import TeacherPage6 from '@/views/result/MLSRD-2026/teacher/Page6.vue';
import TeacherPage7 from '@/views/result/MLSRD-2026/teacher/Page7.vue';
import PageHeader from '@/components/result/MLSRD-2026/PageHeader.vue';
import PageFooter from '@/components/result/MLSRD-2026/PageFooter.vue';

import ResultSwiper from '@/components/result/MLSRD-2026/Swiper.vue'; 
import { SwiperSlide } from 'swiper/vue';

const route = useRoute();

// 교사 결과 데이터
const classData = ref(null);
const loading = ref(true);

const totalPages = 5; // 3p(문해력 상세), 5p(수리력 상세) 제외 
const subHeaderText = ref('로딩중...');

// 서브헤더 텍스트 computed
const computedSubHeaderText = computed(() => {
  return subHeaderText.value;
});

// API 호출 함수
const fetchClassResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code, grade_code, school_cls } = route.query;
    
    if (!exam_id || !schul_code || !grade_code || !school_cls) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('교사 결과 API 요청:', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

    const response = await api.post('/exam/tea/resultClassDetail', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

    if (response.data.success) {
      classData.value = response.data.result;
      console.log('교사 결과 데이터:', classData.value);
      
      // 서브헤더 텍스트 업데이트
      const schoolName = classData.value.schul_nm || '학교';
      const gradeClass = `${grade_code}-${school_cls}`;
      subHeaderText.value = `${schoolName} ${gradeClass} 결과`;
      
      // PDF 파일명 생성을 위해 전역에 저장
      window.currentClassData = classData.value;
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('교사 결과 데이터 조회 실패:', error);
    console.error('error.response:', error.response);
    
    // 시험 미응시 에러 체크 (다양한 경로로 시도)
    if ((error.response && error.response.data && error.response.data.data && error.response.data.data.errorCode === 'RESULT_001') ||
        (error.response && error.response.data && error.response.data.errorCode === 'RESULT_001')) {
      alert('시험에 응시하지 않았습니다.');
      window.close();
      return;
    }
  } finally {
    loading.value = false;
  }
};

// 하위 컴포넌트에서 사용할 수 있도록 provide
provide('classData', classData);
provide('loading', loading);

onMounted(() => {
  fetchClassResult();
});
</script>

<template>
<div class="result-container" style="background-color: white;">
    <!-- 학급 데이터를 PDF 생성기에서 접근할 수 있도록 숨겨진 요소에 저장 -->
    <div v-if="classData" style="display: none;" data-class-data :data-class-data="JSON.stringify(classData)"></div>
    
    <div class="page-wrapper">
      <PageHeader :page-index="0" report-type="teacher" :sub-header-text="computedSubHeaderText" />
      <TeacherPage1 />
      <PageFooter :current-page="1" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="1" report-type="teacher" :sub-header-text="computedSubHeaderText" />
      <TeacherPage2 />
      <PageFooter :current-page="2" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="2" report-type="teacher" :sub-header-text="computedSubHeaderText" />
      <TeacherPage4 />
      <PageFooter :current-page="3" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="3" report-type="teacher" :sub-header-text="computedSubHeaderText" />
      <TeacherPage6 />
      <PageFooter :current-page="4" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="4" report-type="teacher" :sub-header-text="computedSubHeaderText" />
      <TeacherPage7 />
      <PageFooter :current-page="5" :total-pages="totalPages" />
    </div>
  </div>
  <!-- <ResultSwiper
    :totalSlides="totalSlides" 
    studentGroup="서울 학생"
    reportCategory="teacher" 
    examDate="2025년 11월"
    :subHeaderText="subHeaderText">   
    <swiper-slide><TeacherPage1 /></swiper-slide>
    <swiper-slide><TeacherPage2 /></swiper-slide>
    <swiper-slide><TeacherPage4 /></swiper-slide>
    <swiper-slide><TeacherPage6 /></swiper-slide>
    <swiper-slide><TeacherPage7 /></swiper-slide>
  </ResultSwiper>
   -->
</template>

<style>
.result-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 20px 0;
}

.page-wrapper {
  width: 100%;
  min-height: 100vh;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
}

.page-wrapper:last-child {
  border-bottom: none;
}

/* 페이지 콘텐츠 영역 */
.page-wrapper > :nth-child(2) {
  flex: 1;
  padding: 20px;
}

/* PDF 출력 시 */
@media print {
  .result-container {
    gap: 0;
    padding: 0;
  }
  
  .page-wrapper {
    min-height: auto;
    padding: 0;
    border-bottom: none;
    page-break-after: always;
    page-break-inside: avoid;
  }
  
  .page-wrapper:last-child {
    page-break-after: avoid;
  }
  
  .page-wrapper > :nth-child(2) {
    padding: 10px;
  }
}
</style>